/**
 * Image Instance Panel Component
 *
 * Renders a single image generator instance panel, either minimized (sidebar)
 * or expanded (full panel with header, gallery, and progress indicator).
 */

import { memo, useState } from 'react';
import {
  Image as ImageIcon,
  ImagePlus,
  Settings,
  Download,
  Trash2,
  Info,
  Copy,
  ZoomIn,
  Minimize2,
  ChevronRight,
  ChevronDown,
  Save,
  Check,
  X,
  RefreshCw,
  Square,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { InsufficientCreditsMessage } from '@/credits/components/InsufficientCreditsMessage';
import { isInsufficientCreditsText } from '@/credits/guest-trial-cta';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ModelDefinitionListItem } from '@/lib/api';
import { ModelCostLabel, InteractionCostLabel } from '@/credits/components';
import { getModelCostInfo } from '@/lib/model-cost';
import { ImageGenerationProgress } from './ImageGenerationProgress';
import {
  GeneratedImage,
  ImageInstanceConfig,
  ImageInstanceState,
  ImagePreset,
  getImageModelInfo,
  getAspectRatioValue,
  getEstimatedDurationMs,
  isUnsavedPresetName,
  toBasePresetName,
  modelSupportsReferenceImage,
  getActiveModelSettings,
  type ModelSizeCapability,
} from '../types/image-generator.types';
import type { PanelConfigUpdates, SetInstanceModelOptions } from '../hooks/useImageGeneratorState';

export interface ImageInstancePanelProps {
  index: number;
  config: ImageInstanceConfig;
  state: ImageInstanceState;
  images: GeneratedImage[];
  isEnabled: boolean;
  isMinimized: boolean;
  isGenerating: boolean;
  availableModels: ModelDefinitionListItem[];
  modelsLoading: boolean;
  presets: ImagePreset[];
  canMinimize: boolean;
  // Inline save state
  inlineSaveInstance: number | null;
  inlineSavePresetName: string;
  // Callbacks
  onToggleMinimized: (instanceId: number) => void;
  onApplyPreset: (
    instanceId: number,
    preset: ImagePreset | null,
    models?: ModelSizeCapability[],
  ) => void;
  /** Panel-level fields only (see `PanelConfigUpdates`). */
  onUpdateConfig: (instanceId: number, updates: PanelConfigUpdates) => void;
  /** Switches this panel's model, restoring or seeding that model's settings. */
  onSetModel: (instanceId: number, modelName: string, options?: SetInstanceModelOptions) => void;
  onOpenSettings: (instanceId: number) => void;
  onSetLightboxImage: (image: GeneratedImage) => void;
  onSetInfoImage: (image: GeneratedImage) => void;
  onSetInputValue: (value: string) => void;
  onDownload: (image: GeneratedImage) => void;
  onDelete: (requestId: string, instanceId: number) => void;
  onDismissError: (instanceId: number) => void;
  onCancelGeneration: (instanceId: number) => void;
  onOverwritePreset: (instanceId: number) => void;
  onDeletePreset: (presetId: string) => void;
  onUseAsReference: (instanceId: number, image: GeneratedImage) => void;
  onOpenReferenceDialog: (instanceId: number) => void;
  onClearReference: (instanceId: number) => void;
  onStartInlineSave: (instanceId: number) => void;
  onInlineSavePresetNameChange: (value: string) => void;
  onSavePresetInline: (instanceId: number) => void;
  onCancelInlineSave: () => void;
  // Ref
  scrollRef: (el: HTMLDivElement | null) => void;
}

export const ImageInstancePanel = memo(function ImageInstancePanel({
  index: idx,
  config,
  state,
  images,
  isEnabled,
  isMinimized,
  isGenerating,
  availableModels,
  modelsLoading,
  presets,
  canMinimize,
  inlineSaveInstance,
  inlineSavePresetName,
  onToggleMinimized,
  onApplyPreset,
  onUpdateConfig,
  onSetModel,
  onOpenSettings,
  onSetLightboxImage,
  onSetInfoImage,
  onSetInputValue,
  onDownload,
  onDelete,
  onDismissError,
  onCancelGeneration,
  onOverwritePreset,
  onDeletePreset,
  onUseAsReference,
  onOpenReferenceDialog,
  onClearReference,
  onStartInlineSave,
  onInlineSavePresetNameChange,
  onSavePresetInline,
  onCancelInlineSave,
  scrollRef,
}: ImageInstancePanelProps) {
  const [deleteConfirmPresetId, setDeleteConfirmPresetId] = useState<string | null>(null);
  const [presetDropdownOpen, setPresetDropdownOpen] = useState(false);
  const [hoveredPresetName, setHoveredPresetName] = useState<string | null>(null);

  const TRUNCATE_THRESHOLD = 20;

  if (!isEnabled) return null;

  const model = availableModels.find((m) => m.modelName === config.modelName) || availableModels[0];

  // Minimized view
  if (isMinimized) {
    return (
      <div
        className="w-12 flex-shrink-0 bg-muted/30 border-r border-border flex flex-col items-center py-4 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={() => onToggleMinimized(idx)}
      >
        <Button variant="ghost" size="icon" className="h-8 w-8 mb-2" title="Expand instance">
          <ChevronRight className="h-4 w-4" />
        </Button>
        <div
          className="writing-mode-vertical text-xs text-muted-foreground font-medium whitespace-nowrap [writing-mode:vertical-rl] rotate-180 max-h-[120px] overflow-hidden text-ellipsis"
          title={model?.displayName || config.modelName}
        >
          {model?.displayName || config.modelName}
        </div>
        {isGenerating && <div className="mt-2 w-2 h-2 rounded-full bg-primary animate-pulse" />}
      </div>
    );
  }

  // Expanded view
  return (
    <>
      <div className="flex-1 min-w-[300px] flex flex-col border-r border-border last:border-r-0">
        {/* Instance header */}
        <div className="flex-shrink-0 px-3 py-2 border-b border-border bg-muted/20 space-y-1.5">
          {/* Row 1: Minimize + Preset dropdown + Overwrite/Save buttons */}
          <div className="flex items-center gap-1.5 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 flex-shrink-0"
              onClick={() => onToggleMinimized(idx)}
              disabled={!canMinimize}
              title={canMinimize ? 'Hide Panel' : 'Cannot hide last panel'}
            >
              <Minimize2 className="h-4 w-4" />
            </Button>
            {/* Preset selector dropdown */}
            <div className="relative min-w-0 flex-1">
              <DropdownMenu
                open={presetDropdownOpen}
                onOpenChange={(open) => {
                  setPresetDropdownOpen(open);
                  if (!open) setHoveredPresetName(null);
                }}
              >
                <DropdownMenuTrigger asChild disabled={isGenerating}>
                  <Button
                    variant="outline"
                    className="h-auto min-h-[1.75rem] w-full py-1 px-2 justify-between gap-1 text-xs"
                    onMouseEnter={() => {
                      if (
                        !presetDropdownOpen &&
                        toBasePresetName(config.presetName).length > TRUNCATE_THRESHOLD
                      ) {
                        setHoveredPresetName(toBasePresetName(config.presetName));
                      }
                    }}
                    onMouseLeave={() => {
                      if (!presetDropdownOpen) setHoveredPresetName(null);
                    }}
                  >
                    <span className="flex flex-col items-start min-w-0">
                      <span className="truncate w-full">{toBasePresetName(config.presetName)}</span>
                      <span className="text-[10px] leading-none h-3">
                        {isUnsavedPresetName(config.presetName) && (
                          <span className="text-muted-foreground">(Unsaved)</span>
                        )}
                      </span>
                    </span>
                    <ChevronDown className="h-3.5 w-3.5 opacity-50 flex-shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="min-w-[180px] max-w-[300px]" align="start">
                  <DropdownMenuItem
                    className="text-xs cursor-pointer gap-2"
                    onSelect={() => onApplyPreset(idx, null, availableModels)}
                  >
                    <Check
                      className={`h-3 w-3 flex-shrink-0 ${config.presetName === 'Default Preset' ? 'opacity-100' : 'opacity-0'}`}
                    />
                    Default Preset
                  </DropdownMenuItem>
                  {presets.length > 0 && (
                    <>
                      <DropdownMenuSeparator />
                      {presets.map((preset) => {
                        const isActive =
                          config.presetName === preset.name ||
                          toBasePresetName(config.presetName) === preset.name;
                        return (
                          <DropdownMenuItem
                            key={preset.id}
                            className="group/preset text-xs cursor-pointer gap-2 pr-1 min-w-0"
                            onSelect={() => onApplyPreset(idx, preset, availableModels)}
                            onMouseEnter={() => {
                              if (preset.name.length > TRUNCATE_THRESHOLD) {
                                setHoveredPresetName(preset.name);
                              }
                            }}
                            onMouseLeave={() => setHoveredPresetName(null)}
                          >
                            <Check
                              className={`h-3 w-3 flex-shrink-0 ${isActive ? 'opacity-100' : 'opacity-0'}`}
                            />
                            <span className="flex-1 min-w-0 truncate">{preset.name}</span>
                            <button
                              className="opacity-0 group-hover/preset:opacity-100 group-data-[highlighted]/preset:opacity-100 h-5 w-5 flex-shrink-0 flex items-center justify-center rounded text-red-500 hover:bg-red-500/10 transition-opacity"
                              onPointerDown={(e) => e.stopPropagation()}
                              onPointerUp={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.stopPropagation();
                                setPresetDropdownOpen(false);
                                setDeleteConfirmPresetId(preset.id);
                              }}
                              title="Delete preset"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </DropdownMenuItem>
                        );
                      })}
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              {/* Tooltip rendered outside the dropdown to avoid overflow clipping */}
              {hoveredPresetName && (
                <div className="absolute left-0 top-full mt-1 px-2 py-1 bg-popover border border-border rounded shadow-lg text-xs whitespace-nowrap pointer-events-none z-[200]">
                  {hoveredPresetName}
                </div>
              )}
            </div>
            {/* Update (overwrite) existing preset button */}
            {isUnsavedPresetName(config.presetName) &&
              config.originalPresetId &&
              presets.some((p) => p.id === config.originalPresetId) &&
              inlineSaveInstance !== idx && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 flex-shrink-0"
                  onClick={() => onOverwritePreset(idx)}
                  title={`Update "${presets.find((p) => p.id === config.originalPresetId)?.name}"`}
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              )}
            {/* Save as new preset button */}
            {isUnsavedPresetName(config.presetName) && inlineSaveInstance !== idx && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 flex-shrink-0"
                onClick={() => onStartInlineSave(idx)}
                title="Save new preset"
              >
                <Save className="h-4 w-4" />
              </Button>
            )}
            {/* Inline save input */}
            {inlineSaveInstance === idx && (
              <div className="flex items-center gap-1 flex-shrink-0">
                <Input
                  value={inlineSavePresetName}
                  onChange={(e) => onInlineSavePresetNameChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && inlineSavePresetName.trim()) {
                      onSavePresetInline(idx);
                    } else if (e.key === 'Escape') {
                      onCancelInlineSave();
                    }
                  }}
                  placeholder="Preset name"
                  className="h-7 w-24 text-xs"
                  autoFocus
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => onSavePresetInline(idx)}
                  disabled={!inlineSavePresetName.trim()}
                  title="Save preset"
                >
                  <Check className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={onCancelInlineSave}
                  title="Cancel"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
          {/* Row 2: Model selector + Model info + Settings */}
          <div className="flex items-center gap-1">
            <Select
              value={config.modelName}
              onValueChange={(value) => {
                const newModel = availableModels.find((m) => m.modelName === value);
                // Restoring what this panel last had on the destination model —
                // and repairing a size the model cannot accept — is the reducer's
                // job, so every path through a model switch behaves the same.
                onSetModel(idx, value, {
                  provider: newModel?.provider || 'openai',
                  models: availableModels,
                });
              }}
              disabled={isGenerating || modelsLoading}
            >
              <SelectTrigger className="flex-1 min-w-0 h-7 text-xs">
                <SelectValue>
                  {model?.displayName || config.modelName || 'Select model'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {availableModels.length > 0 ? (
                  availableModels.map((modelDef) => {
                    const info = getImageModelInfo(modelDef.modelName, availableModels);
                    return (
                      <SelectItem key={modelDef.modelName} value={modelDef.modelName}>
                        <div className="flex flex-col">
                          <span>{modelDef.displayName}</span>
                          {info && (
                            <span className="text-[10px] text-muted-foreground">
                              {info.company}
                            </span>
                          )}
                          <ModelCostLabel pricing={modelDef.pricing} className="mt-0.5" />
                        </div>
                      </SelectItem>
                    );
                  })
                ) : (
                  <SelectItem value={config.modelName || '_loading'} disabled>
                    Loading models...
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {/* Model info popover */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 flex-shrink-0"
                  title="Model info"
                >
                  <Info className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-3" side="bottom" align="start">
                {(() => {
                  const info = getImageModelInfo(config.modelName, availableModels);
                  const cost = getModelCostInfo(model?.pricing);
                  return info ? (
                    <div className="space-y-2">
                      <div>
                        <p className="font-medium text-sm">
                          {model?.displayName || config.modelName}
                        </p>
                        <p className="text-xs text-muted-foreground">{info.company}</p>
                      </div>
                      <p className="text-xs">{info.description}</p>
                      {cost && (
                        <div>
                          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                            Cost
                          </p>
                          <ModelCostLabel pricing={model?.pricing} className="text-xs" />
                        </div>
                      )}
                      {info.strengths && (
                        <div>
                          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                            Strengths
                          </p>
                          <p className="text-xs text-muted-foreground">{info.strengths}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      No info available for this model.
                    </p>
                  );
                })()}
              </PopoverContent>
            </Popover>
            {/* Add reference image button - only for models that support it */}
            {modelSupportsReferenceImage(config.modelName, availableModels) && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 flex-shrink-0"
                onClick={() => onOpenReferenceDialog(idx)}
                disabled={isGenerating}
                title="Add reference image"
              >
                <ImagePlus className="h-4 w-4" />
              </Button>
            )}
            {/* Settings button for this instance */}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 flex-shrink-0"
              onClick={() => onOpenSettings(idx)}
              disabled={isGenerating}
              title="Preset settings"
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>
          {/* Reference image strip */}
          {config.referenceImage && (
            <div className="flex items-center justify-between gap-1 px-1 py-1 bg-muted/40 rounded">
              <img
                src={config.referenceImage.thumbnailUrl}
                alt="Reference"
                className="h-6 w-6 rounded object-cover border border-border flex-shrink-0"
              />
              <div className="flex items-center gap-1 flex-shrink-0">
                <span className="text-xs text-muted-foreground">Creativity</span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={Math.round((1 - (config.referenceImage.strength ?? 0.5)) * 100)}
                  onChange={(e) => {
                    const creativity = parseInt(e.target.value, 10);
                    const strength = 1 - creativity / 100;
                    onUpdateConfig(idx, {
                      referenceImage: { ...config.referenceImage!, strength },
                    });
                  }}
                  className="w-20 h-1 accent-primary cursor-pointer"
                  title={`Creativity: ${Math.round((1 - (config.referenceImage.strength ?? 0.5)) * 100)}%`}
                />
                <span className="text-[10px] text-muted-foreground">
                  {Math.round((1 - (config.referenceImage.strength ?? 0.5)) * 100)}%
                </span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 flex-shrink-0"
                onClick={() => onClearReference(idx)}
                title="Remove reference"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>

        {/* Error display - outside scroll area so it doesn't cause scrolling */}
        {state.error && (
          <div className="flex-shrink-0 mx-3 mt-3 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
            <p className="font-medium">Error</p>
            {isInsufficientCreditsText(state.error) ? (
              // The shared prompt: the store for a full account, sign-up for a
              // guest whose trial credits are used up.
              <p className="text-xs mt-1">
                <InsufficientCreditsMessage />
              </p>
            ) : (
              <p className="text-xs mt-1">{state.error}</p>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDismissError(idx)}
              className="mt-2 h-6 text-xs"
            >
              Dismiss
            </Button>
          </div>
        )}

        {/* Instance content - scrollable gallery area */}
        <div
          className={`flex-1 p-3 ${images.length > 0 || isGenerating ? 'overflow-y-auto' : 'overflow-hidden'}`}
          ref={scrollRef}
        >
          {/* Image gallery for this instance */}
          {images.length === 0 && !isGenerating ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <ImageIcon className="h-8 w-8 text-muted-foreground/30 mb-1.5" />
              <p className="text-xs text-muted-foreground">No images yet</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {images.map((image) => (
                <div
                  key={image.requestId}
                  className="rounded-lg border border-border bg-card overflow-hidden flex flex-col items-center"
                >
                  {/* Image thumbnail */}
                  <button
                    className="bg-muted relative cursor-pointer group border-0 p-0 self-stretch flex items-center justify-center"
                    tabIndex={0}
                    aria-label={`View full image: ${image.prompt?.substring(0, 50) || 'Generated image'}`}
                    onClick={() => onSetLightboxImage(image)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSetLightboxImage(image);
                      }
                    }}
                  >
                    {image.imageUrl ? (
                      <>
                        <img
                          src={image.imageUrl}
                          alt={
                            image.prompt && image.prompt.length > 100
                              ? `${image.prompt.substring(0, 100)}…`
                              : image.prompt
                          }
                          className="block max-w-full max-h-[50vh] object-contain transition-transform duration-200 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-200 flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white/90 rounded-full p-2 shadow-lg">
                            <ZoomIn className="h-4 w-4 text-gray-800" />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div
                        className="w-full flex items-center justify-center"
                        style={{ aspectRatio: '1/1' }}
                      >
                        <ImageIcon className="h-8 w-8 text-muted-foreground/30" />
                      </div>
                    )}
                  </button>

                  {/* Image details */}
                  <div className="p-2 self-stretch">
                    <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                      {image.prompt}
                    </p>
                    {/* What this image actually cost */}
                    {image.costCredits !== undefined && (
                      <div className="mb-2">
                        <InteractionCostLabel costCredits={image.costCredits} />
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => onDownload(image)}
                          title="Download"
                        >
                          <Download className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => onSetInputValue(image.prompt)}
                          title="Copy prompt"
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-destructive hover:text-destructive"
                          onClick={() => onDelete(image.requestId, idx)}
                          title="Delete"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-1">
                        {modelSupportsReferenceImage(config.modelName, availableModels) && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => onUseAsReference(idx, image)}
                            title="Use as reference"
                          >
                            <ImagePlus className="h-3 w-3" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => onSetInfoImage(image)}
                          title="Image Info"
                        >
                          <Info className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Progress indicator - shows below images while generating */}
              {isGenerating && (
                <div
                  className="relative mt-3 max-h-[50vh] bg-muted rounded-xl border border-border flex items-center justify-center overflow-hidden"
                  style={{ aspectRatio: getAspectRatioValue(getActiveModelSettings(config).size) }}
                  aria-live="polite"
                >
                  <ImageGenerationProgress
                    stage={state.stage}
                    message={state.progressMessage}
                    className="w-full h-full"
                    estimatedDurationMs={getEstimatedDurationMs(config.modelName, availableModels)}
                  />
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute bottom-3 h-10 w-10 rounded-xl shadow-md"
                    onClick={() => onCancelGeneration(idx)}
                    title="Stop generation"
                  >
                    <Square className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Delete preset confirmation dialog */}
      <AlertDialog
        open={deleteConfirmPresetId !== null}
        onOpenChange={(open) => !open && setDeleteConfirmPresetId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Preset</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;
              {presets.find((p) => p.id === deleteConfirmPresetId)?.name}
              &quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleteConfirmPresetId) {
                  const deletedPreset = presets.find((p) => p.id === deleteConfirmPresetId);
                  onDeletePreset(deleteConfirmPresetId);
                  if (deletedPreset && config.presetName === deletedPreset.name) {
                    onApplyPreset(idx, null, availableModels);
                  }
                  if (config.originalPresetId === deleteConfirmPresetId) {
                    onUpdateConfig(idx, { originalPresetId: null });
                  }
                }
                setDeleteConfirmPresetId(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
});
